import React, { useMemo, useRef, useState, useEffect } from 'react';
import { AppointmentMeHey, getMonthDays, getDayAppointments } from './AppointmentMeHeyUtils';
import { formatTimeRange, isToday } from 'react-lib/utils/dateUtils';
import CardInMonthMeHey from './CardAppointmentMeHey';
import { useIntl } from 'react-intl';

interface AppointmentMonthViewMeHeyProps {
  currentDate: Date;
  appointments: AppointmentMeHey[];
  onDayClick?: (date: Date) => void;
  onAppointmentClick?: (appointment: AppointmentMeHey) => void;
  selectedAppointmentId?: number | null;
  showAvailability?: boolean;
  onMoreClick?: (date: Date) => void;
}

const AppointmentMonthViewMeHey: React.FC<AppointmentMonthViewMeHeyProps> = (props) => {
  const intl = useIntl();
  const monthDays = useMemo(
    () => getMonthDays(props.currentDate.getFullYear(), props.currentDate.getMonth()),
    [props.currentDate]
  );

  const dayRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [maxCardsPerDay, setMaxCardsPerDay] = useState<number[]>([]);

  const isCurrentMonth = (day: Date): boolean => {
    return day.getMonth() === props.currentDate.getMonth();
  };

  const dayHeaders = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'];

  // console.log('currentDate: ', currentDate);
  // Calculate how many cards can fit in each day cell
  useEffect(() => {
    const calculateMaxCards = () => {
      const newMaxCards: number[] = [];

      dayRefs.current.forEach((dayRef, index) => {
        if (dayRef) {
          const dayHeight = dayRef.clientHeight;
          // console.log('dayHeight: ', dayHeight);
          const dayNumber = dayRef.querySelector('.day-number');
          // console.log('dayNumber: ', dayNumber);
          const dayNumberHeight = dayNumber ? dayNumber.clientHeight : 20;
          // console.log('dayNumberHeight: ', dayNumberHeight);

          // Get the appointments for this day to check if we need "see more"
          // console.log('appointments: ', appointments);
          // console.log('index: ', index);
          // console.log('monthDays[index]: ', monthDays[index]);
          // console.log('monthDays: ', monthDays);
          if (monthDays[index] === undefined) {
            console.warn('monthDays: ', monthDays);
            console.warn('monthDays[index] is undefined at index:', index);
          }
          const dayAppointments = getDayAppointments(props.appointments || [], monthDays[index]);
          const totalAppointments = dayAppointments.length;

          // Available height for appointments (subtract padding)
          const dayPadding = 8; // top/bottom padding (4px each)
          const appointmentsMarginTop = 3; // .day-rounds margin-top
          const availableHeight = dayHeight - dayNumberHeight - dayPadding - appointmentsMarginTop;

          // Card dimensions from CSS
          // .rounds-card.compact: min-height(14px) + padding-top(1px) + padding-bottom(1px) + margin-bottom(2px) = 18px
          const cardHeight = 26;
          const gap = 2; // .day-rounds gap (spacing between flex items)
          // .more-rounds-card: min-height(16px) + margin-top(2px) = 18px
          const moreCardHeight = 20;

          // Calculate max cards that can fit
          // Formula: For N cards, height needed = N * cardHeight + (N-1) * gap
          // Solving: N = floor((availableHeight + gap) / (cardHeight + gap))
          let maxCards;
          if (totalAppointments > 1) {
            // Reserve space for the "more" card (if needed)
            const heightWithoutMoreCard = availableHeight - moreCardHeight - gap;
            // Calculate how many appointment cards fit (last card has no gap after it)
            maxCards = Math.floor((heightWithoutMoreCard + gap) / (cardHeight + gap));

            // If all appointments fit without needing "more" card, recalculate without reserving space
            if (maxCards >= totalAppointments) {
              maxCards = Math.floor((availableHeight + gap) / (cardHeight + gap));
            }
          } else {
            // Only one appointment, no "more" card needed
            maxCards = Math.floor((availableHeight + gap) / (cardHeight + gap));
          }

          // Ensure at least 1 card is shown
          maxCards = Math.max(1, maxCards);

          // Debug logging (only for days with appointments)
          // if (totalAppointments > 0 && index < 7) { // Log only first week for clarity
          //   console.log(`Day ${index + 1}:`, {
          //     dayHeight,
          //     dayNumberHeight,
          //     availableHeight,
          //     totalAppointments,
          //     maxCards,
          //     willShowMore: totalAppointments > maxCards
          //   });
          // }

          newMaxCards.push(maxCards);
        } else {
          newMaxCards.push(2); // default fallback
        }
      });

      setMaxCardsPerDay(newMaxCards);
    };

    // Calculate on mount and when window resizes
    const timeoutId = setTimeout(calculateMaxCards, 0); // Delay to ensure DOM is ready
    window.addEventListener('resize', calculateMaxCards);

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', calculateMaxCards);
    };
  }, [monthDays, props.appointments]);

  return (
    <div className="month-view">
      {/* Day headers */}
      <div className="calendar-header">
        {dayHeaders.map((day) => (
          <div key={day} className="day-header">
            {day}
          </div>
        ))}
      </div>

      {/* Calendar grid */}
      <div className="calendar-grid">
        {monthDays.map((day, index) => {
          const dayAppointments = getDayAppointments(props.appointments || [], day);
          // console.log('dayAppointments: ', dayAppointments);
          const isCurrentMonthDay = isCurrentMonth(day);
          const isTodayDate = isToday(day);
          const todayStart = new Date();
          todayStart.setHours(0, 0, 0, 0);
          const isPastDay = day < todayStart;
          const maxCards = maxCardsPerDay[index] || 2; // default to 2 if not calculated yet
          const uniqueBlocks = dayAppointments.filter((appointment, position) =>
            appointment.doctor_dsb_id == null ||
            dayAppointments.findIndex((item) => item.doctor_dsb_id === appointment.doctor_dsb_id) === position
          );
          const availabilityKnown = uniqueBlocks.every((appointment) => appointment.availableSlots != null);
          const availableCount = uniqueBlocks.reduce((sum, appointment) => sum + (appointment.availableSlots || 0), 0);
          const selected = uniqueBlocks.some((appointment) =>
            appointment.doctor_dsb_id != null && appointment.doctor_dsb_id === props.selectedAppointmentId
          );
          const hasAppointments = uniqueBlocks.some((appointment) => appointment.ishaveAppointment);
          const scheduleTitle = uniqueBlocks.map((appointment) => [
            appointment.startTime && appointment.endTime
              ? formatTimeRange(appointment.startTime, appointment.endTime)
              : '',
            appointment.title || appointment.doctor || '',
          ].filter(Boolean).join(' ')).filter(Boolean).join('\n');

          return (
            <div
              key={index}
              ref={(el) => (dayRefs.current[index] = el)}
              className={`calendar-day ${!isCurrentMonthDay ? 'other-month' : ''} ${isTodayDate ? 'today' : ''} ${props.showAvailability ? 'calendar-day--availability' : ''} ${props.showAvailability && isPastDay ? 'calendar-day--past' : ''} ${props.showAvailability && selected ? 'calendar-day--availability-selected' : ''}`}
              onClick={() => props.onDayClick && props.onDayClick(day)}
            >
              <div className="day-number">{day.getDate()}</div>
              <div className="day-rounds">
                {props.showAvailability && !isPastDay && uniqueBlocks.length > 0 ? (
                  <button
                    type="button"
                    className={`calendar-availability ${selected ? 'calendar-availability--selected' : ''}`}
                    title={scheduleTitle || undefined}
                    onClick={(event) => {
                      event.stopPropagation();
                      if (uniqueBlocks.length === 1) {
                        props.onAppointmentClick?.(uniqueBlocks[0]);
                      } else {
                        props.onMoreClick?.(day);
                      }
                    }}
                  >
                    {hasAppointments && (
                      <span
                        className="rounds-indicator-dot"
                        role="img"
                        aria-label={intl.formatMessage({ id: 'มีนัดหมาย' })}
                      />
                    )}
                    {availabilityKnown
                      ? intl.formatMessage({ id: 'ว่าง {count} ช่วง' }, { count: availableCount })
                      : intl.formatMessage({ id: 'ไม่ทราบจำนวนช่วงว่าง' })}
                  </button>
                ) : !props.showAvailability && dayAppointments.slice(0, maxCards).map((apt) => (
                  <CardInMonthMeHey
                    key={apt.id}
                    rounds={apt}
                    onClick={(e) => {
                      e?.stopPropagation();
                      props.onAppointmentClick && props.onAppointmentClick(apt);
                    }}
                    compact={true}
                    isSelected={props.selectedAppointmentId === apt.id}
                  />
                ))}
                {!props.showAvailability && dayAppointments.length > maxCards && (
                  <div
                    className="more-rounds-card"
                    onClick={(e) => {
                      e.stopPropagation();
                      props.onMoreClick && props.onMoreClick(day);
                    }}
                  >
                    +{dayAppointments.length - maxCards} เพิ่มเติม
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default AppointmentMonthViewMeHey;